import * as React from 'react'
import { ResolvedOptionColor } from '~/common/styles/enum-colors'

export interface Option<T extends string = string> {
  value: T
  label: string
  /** Secondary line rendered under the label, e.g. why a disabled option can't be picked */
  description?: string
  disable?: boolean
  fixed?: boolean
  startIcon?: React.ReactNode
  endIcon?: React.ReactNode
  /** Resolved color palette — renders a swatch (or tints an icon) as the option's leading mark */
  color?: ResolvedOptionColor | null
  /** DossIcon asset name — renders as the option's leading mark (takes precedence over a swatch) */
  iconName?: string | null
}

export interface OptionGroup {
  label: string
  options: Option[]
}

/** Converts an array of strings to an array of Option objects with matching value and label */
export const stringsToOptions = <T extends string>(values: T[]): Option<T>[] =>
  values.map((v) => ({ value: v, label: v }))

/** Extracts just the values from an array of Option objects */
export const optionsToStrings = <T extends string>(options: Option<T>[]): T[] =>
  options.map((o) => o.value)

/** Ensures a string or Option is converted to a proper Option object */
export function normalizeOption<T extends string>(option: T | Option<T>): Option<T> {
  if (typeof option === 'string') {
    return {
      value: option,
      label: option,
    }
  }
  return option
}

/** Type guard to check if an item is an OptionGroup */
export function isOptionGroup(item: string | Option | OptionGroup): item is OptionGroup {
  return typeof item === 'object' && 'options' in item && Array.isArray(item.options)
}

/** Converts an array of mixed types (string/Option/OptionGroup) into a standardized array of OptionGroups */
export function transToGroupOption(options: (string | Option | OptionGroup)[]): OptionGroup[] {
  if (options.length === 0) {
    return []
  }

  return options.map((option) => {
    if (isOptionGroup(option)) {
      return option
    }
    return {
      label: '',
      options: [normalizeOption(option)],
    }
  })
}
